/**
 * Three-step quote form ("Offerte anfragen", block-form.php).
 *
 * <form data-quote-form data-post-id data-turnstile-key data-form-messages='{…}'>
 *   <ol data-form-stepper><li data-form-stepper-item="1" class="is-current">…</li>…</ol>
 *   <fieldset data-form-step="1">…</fieldset> <fieldset data-form-step="2" hidden>…
 *   <div data-form-turnstile></div>            (last step, only with a site key)
 *   <input type="hidden" name="started_at" data-form-started>
 *   <button data-form-test-fill='{…}'>        (logged-in users only)
 *   <button data-form-prev> <button data-form-next> <button type="submit" data-form-submit>
 *   <div data-form-error hidden> <div data-form-success hidden>…<button data-form-reset></div>
 * </form>
 *
 * Each "next" validates the fields of the current step only (native
 * constraint validation, `.is-invalid` + a message per field). The last step
 * renders a Cloudflare Turnstile widget when a site key exists and submits
 * the whole form as JSON to glunz/v1/quote-request. After sending, the
 * section lands like a jump to its navigation point, and "Neue Anfrage"
 * empties the form for the next request.
 */
import { flowTop, scrollToTarget } from './scroll'
const TURNSTILE_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'
const FIELD_SELECTOR = 'input:not([type="hidden"]), textarea, select'

let turnstileLoader = null

const loadTurnstile = () => {
  if (window.turnstile) return Promise.resolve(window.turnstile)
  turnstileLoader ??= new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = TURNSTILE_SRC
    script.async = true
    script.onload = () => resolve(window.turnstile)
    script.onerror = () => {
      turnstileLoader = null
      reject(new Error('Turnstile could not be loaded'))
    }
    document.head.append(script)
  })
  return turnstileLoader
}

const setup = (form) => {
  const steps = [...form.querySelectorAll('[data-form-step]')]
  if (!steps.length) return

  const stepperItems = [...form.querySelectorAll('[data-form-stepper-item]')]
  const prevButton = form.querySelector('[data-form-prev]')
  const nextButton = form.querySelector('[data-form-next]')
  const submitButton = form.querySelector('[data-form-submit]')
  const errorBox = form.querySelector('[data-form-error]')
  const successBox = form.querySelector('[data-form-success]')
  const turnstileBox = form.querySelector('[data-form-turnstile]')
  const startedField = form.querySelector('[data-form-started]')
  const testFillButton = form.querySelector('[data-form-test-fill]')
  const resetButton = form.querySelector('[data-form-reset]')
  const siteKey = form.dataset.turnstileKey ?? ''
  // The navigation point block-form.php renders in front of its section
  const anchor = form.closest('[data-section]')?.previousElementSibling
  const landingTarget = anchor?.matches('[data-nav-anchor]') ? anchor : form

  let messages = {}
  try {
    messages = JSON.parse(form.dataset.formMessages ?? '{}')
  } catch {
    messages = {}
  }

  // The server's timing checks count from here — restarted for a new request
  let startedAt = 0
  const start = () => {
    startedAt = Date.now()
    if (startedField) startedField.value = String(Math.floor(startedAt / 1000))
  }
  start()

  let current = 0
  let turnstileToken = ''
  let turnstileWidget = null
  let sending = false

  // Field state ---------------------------------------------------------
  const fieldsOf = (step) => [...step.querySelectorAll(FIELD_SELECTOR)]
  const messageOf = (field) => field.parentElement?.querySelector('[data-form-field-message]')

  const markInvalid = (field, message) => {
    field.classList.add('is-invalid')
    field.setAttribute('aria-invalid', 'true')
    const box = messageOf(field)
    if (box) {
      box.textContent = message
      box.hidden = false
    }
  }

  const clearInvalid = (field) => {
    field.classList.remove('is-invalid')
    field.removeAttribute('aria-invalid')
    const box = messageOf(field)
    if (box) {
      box.textContent = ''
      box.hidden = true
    }
  }

  const messageFor = (field) => {
    if (field.validity.valueMissing) return messages.required ?? 'Bitte ausfüllen.'
    if (field.type === 'email') return messages.email ?? messages.invalid ?? ''
    if (field.inputMode === 'numeric') return messages.digits ?? messages.invalid ?? ''
    return messages.invalid ?? ''
  }

  // `step` only steers the picker (quarter hours on the time field) — a time
  // typed off the step is still accepted.
  const isValid = (field) => field.checkValidity() || field.validity.stepMismatch

  const validateStep = (index) => {
    const step = steps[index - 1]
    let firstInvalid = null
    fieldsOf(step).forEach((field) => {
      if (isValid(field)) {
        clearInvalid(field)
        return
      }
      markInvalid(field, messageFor(field))
      firstInvalid ??= field
    })
    firstInvalid?.focus()
    return firstInvalid === null
  }

  const showError = (message) => {
    if (!errorBox) return
    errorBox.textContent = message
    errorBox.hidden = false
  }

  const hideError = () => {
    if (errorBox) errorBox.hidden = true
  }

  // Turnstile -------------------------------------------------------------
  const renderTurnstile = () => {
    if (!siteKey || !turnstileBox || turnstileWidget !== null) return
    loadTurnstile()
      .then((turnstile) => {
        if (turnstileWidget !== null || !turnstile) return
        turnstileWidget = turnstile.render(turnstileBox, {
          sitekey: siteKey,
          theme: 'light',
          size: 'flexible',
          callback: (token) => {
            turnstileToken = token
          },
          'expired-callback': () => {
            turnstileToken = ''
          },
          'error-callback': () => {
            turnstileToken = ''
          },
        })
      })
      .catch(() => showError(messages.error ?? ''))
  }

  const resetTurnstile = () => {
    turnstileToken = ''
    if (turnstileWidget !== null) window.turnstile?.reset(turnstileWidget)
  }

  // Steps -----------------------------------------------------------------
  const scrollToForm = () => {
    const offset = parseFloat(getComputedStyle(form).scrollMarginTop) || 0
    // Two frames later: stack.js has compensated the section's new height by
    // then, so its instant scroll cannot cancel this one.
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        window.scrollTo({ top: Math.max(0, flowTop(form) - offset) })
      })
    )
  }

  // After sending (and for a new request) the section lands where its
  // navigation point puts it: the headline at the usual distance below the
  // header, the message beside it (desktop) or right below it (mobile).
  const scrollToSection = () => {
    requestAnimationFrame(() => requestAnimationFrame(() => scrollToTarget(landingTarget)))
  }

  const show = (index, { scroll = true } = {}) => {
    const previous = current
    current = Math.min(Math.max(index, 1), steps.length)
    const isLast = current === steps.length

    steps.forEach((step, i) => {
      const active = i + 1 === current
      const entering = active && step.hidden && previous !== 0
      step.hidden = !active
      if (entering) {
        step.classList.remove('is-entering', 'is-backward')
        step.classList.toggle('is-backward', current < previous)
        step.classList.add('is-entering')
      }
    })
    stepperItems.forEach((item) => {
      const number = Number(item.dataset.formStepperItem)
      item.classList.toggle('is-current', number === current)
      item.classList.toggle('is-done', number < current)
      if (number === current) item.setAttribute('aria-current', 'step')
      else item.removeAttribute('aria-current')
    })
    if (prevButton) prevButton.hidden = current === 1
    if (nextButton) nextButton.hidden = isLast
    if (submitButton) submitButton.hidden = !isLast

    hideError()
    if (isLast) renderTurnstile()
    if (scroll) {
      fieldsOf(steps[current - 1])[0]?.focus({ preventScroll: true })
      scrollToForm()
    }
  }

  const next = () => {
    if (validateStep(current)) show(current + 1)
  }

  // Stepper clicks: back is always allowed, forward only past valid steps —
  // the first invalid step in between is shown instead.
  const goTo = (target) => {
    if (target === current) return
    if (target < current) {
      show(target)
      return
    }
    for (let i = current; i < target; i += 1) {
      if (!validateStep(i)) {
        if (i !== current) show(i)
        return
      }
    }
    show(target)
  }

  // Submit ----------------------------------------------------------------
  const stepOf = (field) => steps.findIndex((step) => step.contains(field)) + 1

  const applyServerErrors = (errors) => {
    let firstStep = 0
    Object.entries(errors).forEach(([name, message]) => {
      const field = form.elements.namedItem(name)
      if (!(field instanceof HTMLElement)) return
      markInvalid(field, String(message))
      const step = stepOf(field)
      if (step && (!firstStep || step < firstStep)) firstStep = step
    })
    if (firstStep && firstStep !== current) show(firstStep)
  }

  const showSuccess = () => {
    form
      .querySelectorAll('[data-form-stepper], [data-form-step], [data-form-actions]')
      .forEach((el) => {
        el.hidden = true
      })
    hideError()
    if (successBox) {
      successBox.hidden = false
      successBox.focus({ preventScroll: true })
    }
    scrollToSection()
  }

  // "Neue Anfrage": an empty form on step 1, with a fresh start time and
  // Turnstile token — the previous ones belong to the request just sent.
  const reset = () => {
    form.reset()
    steps.flatMap(fieldsOf).forEach(clearInvalid)
    start()
    resetTurnstile()
    if (successBox) successBox.hidden = true
    form.querySelectorAll('[data-form-stepper], [data-form-actions]').forEach((el) => {
      el.hidden = false
    })
    show(1, { scroll: false })
    fieldsOf(steps[0])[0]?.focus({ preventScroll: true })
    scrollToSection()
  }

  // Test data (logged-in users only): every step at once, current step kept
  const fillTestData = () => {
    let values = {}
    try {
      values = JSON.parse(testFillButton?.dataset.formTestFill ?? '{}')
    } catch {
      values = {}
    }
    steps.flatMap(fieldsOf).forEach((field) => {
      if (typeof values[field.name] !== 'string') return
      field.value = values[field.name]
      clearInvalid(field)
    })
    hideError()
  }

  const payload = () => {
    const data = Object.fromEntries(new FormData(form).entries())
    data.post_id = Number(form.dataset.postId) || 0
    data.elapsed = Math.floor((Date.now() - startedAt) / 1000)
    data.turnstile = turnstileToken
    return data
  }

  const submit = async () => {
    if (sending) return
    if (!validateStep(current)) return
    if (siteKey && !turnstileToken) {
      showError(messages.captcha ?? '')
      return
    }

    sending = true
    hideError()
    const label = submitButton?.textContent ?? ''
    if (submitButton) {
      submitButton.disabled = true
      submitButton.textContent = messages.sending ?? label
    }

    try {
      const restUrl = window.php_vars?.restUrl ?? '/wp-json/'
      const response = await fetch(`${restUrl}glunz/v1/quote-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload()),
      })
      const result = await response.json().catch(() => ({}))

      if (response.ok && result.success === true) {
        showSuccess()
        return
      }

      if (result.errors && typeof result.errors === 'object') applyServerErrors(result.errors)
      showError(result.message || messages.error || '')
      resetTurnstile()
    } catch {
      showError(messages.error ?? '')
      resetTurnstile()
    } finally {
      sending = false
      if (submitButton) {
        submitButton.disabled = false
        submitButton.textContent = label
      }
    }
  }

  // Wiring ----------------------------------------------------------------
  nextButton?.addEventListener('click', next)
  prevButton?.addEventListener('click', () => show(current - 1))
  resetButton?.addEventListener('click', reset)
  testFillButton?.addEventListener('click', fillTestData)
  form
    .querySelectorAll('[data-form-stepper-go]')
    .forEach((button) =>
      button.addEventListener('click', () => goTo(Number(button.dataset.formStepperGo)))
    )
  steps.forEach((step) =>
    step.addEventListener('animationend', () => step.classList.remove('is-entering', 'is-backward'))
  )
  form.addEventListener('submit', (event) => {
    event.preventDefault()
    // Enter in a text field submits the form even while the submit button is
    // hidden — treat it as "next" until the last step is reached.
    if (current < steps.length) next()
    else submit()
  })
  form.addEventListener('input', (event) => {
    if (event.target instanceof HTMLElement && event.target.matches(FIELD_SELECTOR)) {
      clearInvalid(event.target)
    }
  })

  show(1, { scroll: false })
}

document.querySelectorAll('[data-quote-form]').forEach(setup)
